import React, { useMemo, useState } from 'react';
import {
  Grid,
  GridColumn,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid';
import { CompositeFilterDescriptor, SortDescriptor, filterBy, orderBy } from '@progress/kendo-data-query';
import { Button } from '@progress/kendo-react-buttons';
import '@progress/kendo-theme-fluent/dist/all.css';
import { FormResponse } from '../../data/formData';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';

interface FormResponsesGridProps {
  data: FormResponse[];
  formName: string;
  automationUrl: string;
  onRowClick: (response: FormResponse) => void;
}

/**
 * A response flattened for the grid. Kendo filters and sorts against the value
 * at `field`, so every filterable column needs a plain primitive here rather
 * than a nested object - `employeeCreatedby` as an object could never match a
 * text filter, and the formatted job reference lives only in the cell renderer.
 */
interface FormResponseRow {
  formResponseId: number;
  jobReference: string;
  clientName: string;
  siteAddress: string;
  createdDate: Date | null;
  createdByName: string;
  /** The untouched response, for the row click and the report action. */
  response: FormResponse;
}

function FormResponsesGrid({ data, formName, automationUrl, onRowClick }: FormResponsesGridProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [page, setPage] = useState({ skip: 0, take: 10 });
  const [sort, setSort] = useState<SortDescriptor[]>([]);
  const [filter, setFilter] = useState<CompositeFilterDescriptor | undefined>(undefined);
  const [generatingReport, setGeneratingReport] = useState<number | null>(null);

  const rows = useMemo<FormResponseRow[]>(
    () =>
      (data || []).map((response) => ({
        formResponseId: response.formResponseId,
        // Prefer the reference the API now sends; fall back to padding the raw
        // ref the same way for anything served by an older build. Note this is
        // deliberately not JobNumberFormatter, which pads to 3 digits ("J1842")
        // and would show a different reference here than the rest of the portal.
        jobReference:
          response.jobReference ||
          (response.job?.ref ? `J${String(response.job.ref).padStart(6, '0')}` : ''),
        clientName: response.clientName || '',
        siteAddress: response.siteAddress || '',
        createdDate: response.createdDate ? new Date(response.createdDate) : null,
        createdByName: response.employeeCreatedby?.employeeName || '',
        response,
      })),
    [data]
  );

  // Filter, then sort, then page - paging last so the count reflects the filter.
  const filteredRows = filter ? filterBy(rows, filter) : rows;
  const sortedRows = orderBy(filteredRows, sort);
  const pagedRows = sortedRows.slice(page.skip, page.skip + page.take);

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPage({
      skip: event.page.skip,
      take: event.page.take
    });
  };

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(event.filter);
    // A filter can shrink the list past the current page, which would otherwise
    // leave the user on an empty one.
    setPage((prev) => ({ ...prev, skip: 0 }));
  };

  const handleSortChange = (event: GridSortChangeEvent) => {
    setSort(event.sort);
  };

  const handleRowClick = (event: any) => {
    onRowClick(event.dataItem.response);
  };

  const formatDate = (date: Date | null) =>
    date ? date.toLocaleDateString('en-GB') : '-';

  const handleGenerateReport = async (formResponseId: number) => {
    if (!automationUrl) {
      showToast('No automation URL configured for this form', 'error');
      return;
    }

    try {
      setGeneratingReport(formResponseId);
      
      await axios.post(automationUrl, {
        FormResponseId: formResponseId
      });

      showToast('Report generation started successfully', 'success');
    } catch (error: any) {
      console.error('Error generating report:', error);
      showToast('Failed to generate report', 'error');
    } finally {
      setGeneratingReport(null);
    }
  };

  const JobLinkCell = (props: any) => {
    const row: FormResponseRow = props.dataItem;
    const jobId = row.response.jobId;

    if (!jobId) {
      return <td>-</td>;
    }

    return (
      <td>
        {/* A real href, so middle-click and "open in new tab" work; the click
            handler keeps an ordinary left click inside the SPA. */}
        <a
          href={`/jobs/?id=${jobId}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            navigate(`/jobs/?id=${jobId}`);
          }}
          style={{ color: '#0078d4', textDecoration: 'none', fontWeight: 600 }}
        >
          {row.jobReference || jobId}
        </a>
      </td>
    );
  };

  return (
    <div className="jobs-kendo-fluent-theme">
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginBottom: '24px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        border: '1px solid #edebe9'
      }}>
        <h2 style={{ 
          fontSize: '1.25rem', 
          fontWeight: '600',
          margin: 0,
          color: '#323130'
        }}>
          Form Responses: {formName}
        </h2>
      </div>

      {/* Kendo Grid */}
      <Grid
        data={pagedRows}
        skip={page.skip}
        take={page.take}
        total={filteredRows.length}
        pageable={{
          buttonCount: 5,
          info: true,
          type: 'numeric',
          pageSizes: [10, 25, 50, 100],
          previousNext: true
        }}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        sortable={true}
        sort={sort}
        onSortChange={handleSortChange}
        filterable={true}
        filter={filter}
        onFilterChange={handleFilterChange}
        resizable={true}
        reorderable={true}
        style={{
          height: 'calc(100vh - 300px)',
          minHeight: '400px',
          cursor: 'pointer'
        }}
      >
        <GridColumn
          field="jobReference"
          title="Job"
          width="130px"
          cell={JobLinkCell}
        />
        <GridColumn
          field="clientName"
          title="Client"
          width="220px"
          cell={(props) => <td>{props.dataItem.clientName || '-'}</td>}
        />
        <GridColumn
          field="siteAddress"
          title="Site"
          width="280px"
          cell={(props) => (
            <td title={props.dataItem.siteAddress}>{props.dataItem.siteAddress || '-'}</td>
          )}
        />
        <GridColumn
          field="createdDate"
          title="Created Date"
          width="150px"
          filter="date"
          format="{0:dd/MM/yyyy}"
          cell={(props) => (
            <td>{formatDate(props.dataItem.createdDate)}</td>
          )}
        />
        <GridColumn
          field="createdByName"
          title="Created By"
          width="200px"
          cell={(props) => (
            <td>{props.dataItem.createdByName || '-'}</td>
          )}
        />
        <GridColumn
          title="Actions"
          width="150px"
          filterable={false}
          sortable={false}
          cell={(props) => (
            <td>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleGenerateReport(props.dataItem.formResponseId);
                }}
                fillMode="solid"
                themeColor="info"
                size="small"
                disabled={!automationUrl || generatingReport === props.dataItem.formResponseId}
                title={
                  automationUrl
                    ? 'Send this response to the form’s automation'
                    : 'No Automation URL is configured for this form'
                }
              >
                {generatingReport === props.dataItem.formResponseId ? 'Generating...' : 'Generate Report'}
              </Button>
            </td>
          )}
        />
      </Grid>
    </div>
  );
}

export default FormResponsesGrid;
